/**
 * Vinyl — the turning record of «Сейчас играет», with the cover or the house picture in its
 * middle, and a finger on it.
 *
 * Since task 20 it lies on the deck (`components/Deck.tsx`): the frame that takes the finger is
 * the whole platter, and the record in its middle is the small one of 45.
 *
 * Task 09. `spec.md` «Пластинка». Named `Vinyl` and not `Record` so the component does not
 * shadow TypeScript's own `Record<K, V>` in every file that draws it.
 *
 * ## Two turns, stacked
 *
 * The disc turns by itself — a CSS animation at 45 or 33⅓ a minute (task 21) and at the tempo's
 * pace (task 23), frozen where it stands on a pause (`styles/deck.css`). Round it, the plate carries the angle the finger has given the
 * record, in the `--grab` property. A finger on the record freezes the disc's own turn and
 * moves the plate 1:1 with itself; released, the plate keeps its angle — the record goes on
 * from where the hand left it, not from where the finger landed — and the disc's own turn picks
 * up again over the same 0,3 s the sound takes to come back to its tempo. That is task 09's
 * touch; under the scratch the voice turns the plate instead — see the last part.
 *
 * **The angle is written onto the node, not through React.** A phone reports a moving finger
 * up to 120 times a second; a render of the screen per move is exactly the cost 1:1 cannot
 * afford (`spec.md` «Пластинка»: «угол пишется в CSS-переменную без перерисовки React»). The
 * same goes for `data-held`: the only renders a touch causes are none.
 *
 * ## What a touch takes, and gives back on every way out
 *
 * A touch takes three things: the element's speed (`engine/speed.ts`, through the player), a
 * stillness timer (`lib/platter.ts`) and a hold on Telegram's vertical swipes
 * (`lib/telegram.ts`). All three are given back by one function, on each of five ways out:
 * the finger lifts, the system cancels the touch, the pointer capture is lost, the screen is
 * unmounted under the finger, and «уменьшение движения» is switched on under the finger. A way
 * out that forgot one of them leaves a silent record (speed 0) or an app whose swipes a
 * released record still holds.
 *
 * ## «Уменьшение движения»
 *
 * The phone's setting, read and kept current. With it the disc does not turn (the stylesheet
 * says so too) and the frame gets no pointer handlers at all — `spec.md`: «жест не
 * устанавливается, управление — только кнопками».
 *
 * ## Two hands in one (task 18)
 *
 * A finger that lands asks the scratch first (`engine/scratch.ts`, `grab`). With the track
 * ready, the scratch takes the touch: the element goes silent, the voice follows the record,
 * and the finger's travel — the same travel the plate turns by — goes to the voice instead of a
 * speed, with each move's time stamp: the voice follows the finger by its own clock, not in
 * jumps. Without it, the touch is task 09's table, exactly as before. Either way the five ways
 * out are the same five. The lift of a finger is the one moment a screen opened from a link has
 * a tap to wake the sound of the scratch with (`prime`); the landing is not a tap to the browser.
 *
 * ## The record is the voice's picture (since 2026-09-29)
 *
 * Under the scratch the plate is turned by the voice, not by the finger: every frame, by where
 * the voice is (`scratch().voiceAt()`), from the touch until the element has the sound again —
 * through the return after the lift too, while `data-voiced` holds the disc's own turn. That is
 * how the prototype drew its record, from its sound, and so the owner saw record and sound
 * together there and apart here: a voice chases the finger and is held to six times, and after
 * the lift the disc spun up on a clock of its own while the voice came back by its motor — or, a
 * rewind, spun on backwards. `requestAnimationFrame` draws it: the picture is for eyes only, and
 * nothing waits on it in the background (`spec.md` «Плеер» keeps it from progress and the queue).
 *
 * ## The face of the record (since 2026-09-30)
 *
 * The middle of the record is one of twelve labels, or the track's cover, with the 45's adapter
 * that suits it (`lib/labels.ts`). Which one is the deck's to keep (`components/Deck.tsx`): its
 * holder shows the same adapter at 33. This component tells it of a tap — a finger that lands on
 * the record itself, not on the platter round the small one, and lifts within {@link TAP_MS}
 * without straying {@link TAP_SLOP_PX}: a finger that moved is a scratch, one that stayed is a
 * hold. It is still a touch of the record — under a playing track the sound stops for the moment
 * of it, as a record does under a finger (the owner's choice, 2026-09-30).
 */

import type { JSX, PointerEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { player } from '../engine/player';
import { scratch } from '../engine/scratch';
import type { ScratchHand } from '../engine/scratch';
import { RETURN_MS, RETURN_STEPS } from '../engine/speed';
import { adapterFor } from '../lib/labels';
import type { LabelKind, RecordFace } from '../lib/labels';
import { createPlatterGesture } from '../lib/platter';
import type { PlatterGesture } from '../lib/platter';
import type { DeckSide } from '../lib/pult-layout';
import { TURN_S, nominalDegPerS } from '../lib/records';
import type { RecordSize } from '../lib/records';
import { telegram } from '../lib/telegram';
import { leaveDeck, takeDeck, tempoRate, useDeck } from '../store/deck';

import { Cover } from './Cover';
import { ADAPTER_ART } from './labelArt';
import { turnAt, useDeckTurn, useReducedMotion } from './motion';
import { RecordLabel } from './RecordLabel';

export const RECORD_LABEL = 'Пластинка';

/** A finger that lifts within this long, without straying, tapped the record. */
export const TAP_MS = 300;

/** …and strayed no further than this from where it landed, in pixels. */
export const TAP_SLOP_PX = 10;

/** Each record across, as a share of the platter the finger lands on (`styles/deck.css`, `.record`). */
const RECORD_SHARE: Readonly<Record<RecordSize, number>> = { '33': 0.9157, '45': 0.5271 };

export interface VinylProps {
  /** A cover URL from the server, or `null` for the house picture. */
  readonly cover: string | null;
  /** The sound is going: the record turns. */
  readonly spinning: boolean;
  /** Task 21: the big record at 33 or the small one at 45. */
  readonly size: RecordSize;
  /** Written into «Ямайская» and «Студия» when either is on the record. */
  readonly title: string;
  readonly performer: string;
  /** What the middle shows: a label, or the cover (`lib/labels.ts`). The deck's to choose. */
  readonly face: RecordFace;
  /** A tap on the record: the deck draws the next face. */
  readonly onTap: () => void;
}

/** A finger on the record: which pointer, what it is doing, where it has put the record. */
interface Grip {
  readonly pointerId: number;
  /**
   * Task 57: the deck the record was when the finger landed. The active deck may change under it —
   * at the middle of a transition — and everything the touch took is given back to this one.
   */
  readonly side: DeckSide;
  readonly gesture: PlatterGesture;
  /** Gives Telegram its vertical swipes back (`lib/telegram.ts`, `holdVerticalSwipes`). */
  readonly releaseSwipes: () => void;
  /** The record's angle under this finger, in degrees: where it started plus the travel. */
  angle: number;
  /** The time stamp of the move being read: the scratch glides by the finger's own clock. */
  movedAt: number;
  /** Task 18: the scratch has this touch; `undefined` for task 09's table. */
  readonly hand: ScratchHand | undefined;
  /** Where and when the finger landed, and whether on the record itself: is it a tap? */
  readonly landed: {
    readonly x: number;
    readonly y: number;
    readonly at: number;
    readonly onRecord: boolean;
  };
  /** The finger has gone further than {@link TAP_SLOP_PX} from where it landed. */
  strayed: boolean;
}

export function Vinyl({
  cover,
  spinning,
  size,
  title,
  performer,
  face,
  onTap,
}: VinylProps): JSX.Element {
  const reducedMotion = useReducedMotion();
  const grip = useRef<Grip | undefined>(undefined);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const plateRef = useRef<HTMLDivElement | null>(null);
  const discRef = useRef<HTMLDivElement | null>(null);
  /** Where hands have left the record, over every touch so far. */
  const restAngle = useRef(0);
  /** The frame the voice's picture waits for, while the voice draws the record. */
  const drawing = useRef<number | undefined>(undefined);
  const spinUp = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const pitch = useDeck((s) => s.pitch);
  const turning = useMemo(() => [discRef], []);
  // 45 became 33, or the tempo moved: the record turns on from its angle, at the new pace.
  useDeckTurn(turning, size, pitch);

  const stopSpinUp = useCallback((): void => {
    if (spinUp.current !== undefined) clearInterval(spinUp.current);
    spinUp.current = undefined;
  }, []);

  const stopDrawing = useCallback((): void => {
    if (drawing.current !== undefined) cancelAnimationFrame(drawing.current);
    drawing.current = undefined;
  }, []);

  /**
   * Gives back everything a touch took, and keeps the angle it left. `undefined` when no
   * finger is on the record — so a second way out, like the lost capture that follows a lift,
   * finds nothing to do.
   */
  const letGo = useCallback((): Grip | undefined => {
    const current = grip.current;
    if (current === undefined) return undefined;
    grip.current = undefined;
    // The swipes first: whatever else fails on the way out, the hold must not outlive the touch.
    current.releaseSwipes();
    current.gesture.end();
    if (current.hand === undefined) player().speedOf(current.side)?.release();
    else current.hand.release();
    leaveDeck('record', current.side);
    restAngle.current = current.angle;
    if (frameRef.current !== null) delete frameRef.current.dataset.held;
    return current;
  }, []);

  // Unmounted under a finger: the speed must not stay where the finger left it.
  useEffect(
    () => () => {
      stopSpinUp();
      stopDrawing();
      letGo();
    },
    [letGo, stopSpinUp, stopDrawing],
  );

  // «Уменьшение движения» switched on under a finger takes the handlers away, so no lift will
  // ever arrive: the touch ends here.
  useEffect(() => {
    if (reducedMotion) letGo();
  }, [reducedMotion, letGo]);

  /**
   * The disc's own turn, from standing to full speed over {@link RETURN_MS} — the time the
   * sound takes to come back to its tempo, on a timer of its own. Full speed is the tempo's
   * (task 23): at +8 % the record turns 8 % faster, as the sound does. Web Animations only: where
   * the browser has none, the turn resumes at full speed at once, which is only less smooth.
   */
  function spinUpDisc(): void {
    stopSpinUp();
    const disc = [discRef.current];
    const full = tempoRate(useDeck.getState().pitch);
    let step = 0;
    turnAt(disc, 0);
    spinUp.current = setInterval(() => {
      step += 1;
      if (step >= RETURN_STEPS) {
        stopSpinUp();
        turnAt(disc, full);
        return;
      }
      turnAt(disc, (full * step) / RETURN_STEPS);
    }, RETURN_MS / RETURN_STEPS);
  }

  /**
   * The plate turned by the voice every frame: `fromAngle` where the voice was at `fromS`, one
   * turn of the record per turn of the track. When the element has the sound again, the plate
   * keeps its angle and the disc's own turn goes on — at full speed, as the voice was.
   */
  function drawFromVoice(fromAngle: number, fromS: number, side: DeckSide): void {
    stopDrawing();
    const frame = frameRef.current;
    if (frame !== null) frame.dataset.voiced = 'true';
    const turn = TURN_S[size];
    const draw = (): void => {
      const atS = scratch().voiceAt(undefined, side);
      if (atS === undefined) {
        drawing.current = undefined;
        if (frameRef.current !== null) delete frameRef.current.dataset.voiced;
        return;
      }
      const angle = fromAngle + ((atS - fromS) / turn) * 360;
      restAngle.current = angle;
      if (grip.current !== undefined) grip.current.angle = angle;
      plateRef.current?.style.setProperty('--grab', `${String(angle)}deg`);
      drawing.current = requestAnimationFrame(draw);
    };
    draw();
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>): void {
    // One finger at a time, and only a touch or the main button of a mouse.
    if (grip.current !== undefined || event.isPrimary === false || event.button !== 0) return;
    const frame = event.currentTarget;
    const box = frame.getBoundingClientRect();
    // No layout, no centre to measure angles from: the touch simply does not start.
    if (box.width <= 0 || box.height <= 0) return;
    // One hand at a time: a needle in the other hand keeps the record free (task 22).
    const side = useDeck.getState().active;
    if (!takeDeck('record', side)) return;
    stopSpinUp();
    // A touch during the voice's return takes the record where the voice has drawn it.
    stopDrawing();
    const start = restAngle.current;
    // The scratch first: it takes the element's speed itself when it takes the touch.
    const hand = scratch().grab(event.timeStamp, side);
    const centre = { x: box.left + box.width / 2, y: box.top + box.height / 2 };
    const reach = (box.width * RECORD_SHARE[size]) / 2;
    const current: Grip = {
      pointerId: event.pointerId,
      side,
      angle: start,
      movedAt: event.timeStamp,
      landed: {
        x: event.clientX,
        y: event.clientY,
        at: event.timeStamp,
        onRecord: Math.hypot(event.clientX - centre.x, event.clientY - centre.y) <= reach,
      },
      strayed: false,
      hand,
      releaseSwipes: telegram.holdVerticalSwipes(),
      gesture: createPlatterGesture({
        // Under the scratch the finger is a place in the track, not a speed.
        onRate: (rate) => {
          if (hand === undefined) player().speedOf(side)?.set(rate);
        },
        // The finger is measured against the record that is on the deck.
        nominalDegPerS: nominalDegPerS(size),
        onTurn: (travel) => {
          // Under the scratch the voice draws the record; the finger only tells it where to go.
          if (hand !== undefined) {
            hand.turn(travel, current.movedAt);
            return;
          }
          current.angle = start + travel;
          plateRef.current?.style.setProperty('--grab', `${String(current.angle)}deg`);
        },
        now: () => performance.now(),
      }),
    };
    grip.current = current;
    frame.dataset.held = 'true';
    try {
      frame.setPointerCapture(event.pointerId);
    } catch {
      // A webview without pointer capture: the touch still works while the finger stays on.
    }
    if (hand === undefined) player().speedOf(side)?.touch();
    else drawFromVoice(start, scratch().voiceAt(undefined, side) ?? 0, side);
    current.gesture.down({ x: event.clientX, y: event.clientY }, centre);
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>): void {
    const current = grip.current;
    if (current?.pointerId !== event.pointerId) return;
    current.movedAt = event.timeStamp;
    const { landed } = current;
    if (Math.hypot(event.clientX - landed.x, event.clientY - landed.y) > TAP_SLOP_PX) {
      current.strayed = true;
    }
    current.gesture.move({ x: event.clientX, y: event.clientY });
  }

  function onPointerEnd(event: PointerEvent<HTMLDivElement>): void {
    if (grip.current?.pointerId !== event.pointerId) return;
    const left = letGo();
    // Under the scratch the voice goes on drawing the record; task 09's disc spins up by itself.
    if (left !== undefined && left.hand === undefined) spinUpDisc();
  }

  function onPointerUp(event: PointerEvent<HTMLDivElement>): void {
    const current = grip.current;
    onPointerEnd(event);
    // A tap on the record: the next face, drawn at random. One render, after the finger.
    if (
      current?.pointerId === event.pointerId &&
      current.landed.onRecord &&
      !current.strayed &&
      event.timeStamp - current.landed.at <= TAP_MS
    ) {
      onTap();
    }
    // A lift is a tap to the browser: a screen opened from a link wakes the scratch here.
    scratch().prime();
  }

  function label(kind: LabelKind): JSX.Element {
    return (
      <RecordLabel
        className="recordCover recordLabel"
        kind={kind}
        title={title}
        performer={performer}
        size={size}
      />
    );
  }

  const gestureHandlers = reducedMotion
    ? {}
    : {
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel: onPointerEnd,
        onLostPointerCapture: onPointerEnd,
      };

  return (
    <div
      ref={frameRef}
      className="deckPart recordFrame"
      role="img"
      aria-label={RECORD_LABEL}
      {...gestureHandlers}
    >
      <div ref={plateRef} className="record" data-size={size}>
        <div
          ref={discRef}
          className="recordDisc"
          data-size={size}
          data-spinning={spinning && !reducedMotion ? 'true' : 'false'}
        >
          {face === 'cover' ? (
            // A cover that is missing or does not load stands in as «Ямайская», as before.
            <Cover src={cover} className="recordCover" fallback={() => label('jamaican')} />
          ) : (
            label(face)
          )}
          {size === '45' && (
            <img
              className="recordAdapter"
              src={ADAPTER_ART[adapterFor(face)]}
              alt=""
              draggable={false}
              data-adapter={adapterFor(face)}
            />
          )}
        </div>
      </div>
      <div className="recordSheen" data-size={size} />
    </div>
  );
}
